/**
 * The resident governor: a rule, over a public record.
 *
 * §13 gives a governor a three-step season — *"**Read** — petitions, offers,
 * foreign notes, the Chronicle, the forecast. **Decide** — priority list, tithes,
 * one or two siting/treaty/ops moves. **Write** — and this is most of the game"* —
 * and this class is the middle step, with the first step's inputs narrowed to what
 * this build publishes and the third step missing entirely, because there is no
 * mail.
 *
 * It is a **pure function** of what it is handed, and that is the load-bearing
 * property rather than a stylistic one:
 *
 *   - **It holds no state, so it needs no snapshot.** Every input is either the
 *     Chronicle (which is inside the district's snapshot already) or a fact about
 *     the map (which is the map). A district restored from a save is therefore
 *     governed identically to one that was never interrupted, and nothing had to be
 *     added to `DistrictSnapshot`, hashed, versioned or re-recorded to make that
 *     true. A governor with a memory would have been a governor whose memory was a
 *     save-format decision.
 *   - **It is auditable in the only sense the design cares about.** Hand the same
 *     record to two machines and they levy the same tithe, which is what §4's
 *     *"witnessing does not change the totals"* has to mean once somebody above the
 *     district is doing the deciding.
 *   - **It cannot see a colony, by construction.** Its inputs are `SeasonRecord`s,
 *     which cannot contain an ant (ADR-012, ADR-023, pinned by `purity.test.ts`),
 *     and a list of coordinates. That is conventions §8's *type-level* Law 7: to
 *     give a governor an ecology figure you would first have to put one in the
 *     public record, in a diff. §18's last line is *"There is no verb, on either
 *     list, that touches an ant"*, and the way to keep it true is to make the
 *     sentence unspeakable rather than to remember not to speak it.
 *
 * ## Read, in this build
 *
 * The reading is the Chronicle plus three map facts. It is assembled by
 * `GovernorSeat`, for the reason every manager in `packages/core` is handed a
 * reading rather than a district (conventions §1): the object that assembles it is
 * allowed to know what a district is, and the object that *decides* is not.
 *
 * ## Decide, in this build
 *
 * Four moves, in this order, and each one is either a bound from the brief or a
 * function of a measured quantity:
 *
 * | Move | What decides it |
 * |---|---|
 * | post prices | the brief's schedule for the season now opening — the world's half |
 * | set the tithe | the polity's unmet appetite over the district's measured tax base |
 * | force-place a facility | an appetite still unmet, and ground the footprint fits on |
 * | route freight | the brief's rate, once the map has two interchanges |
 *
 * Nothing here computes an opinion. There is no reputation, no patience, no
 * memory of what this district did last time, and no term that is not a dollar,
 * a season or a cell — §19's list of absences, applied to the actor at the top.
 */

import { FacilityTypeName } from '@antification/protocol';
import { BRIEF_FACILITY, GovernorBrief, PricePosting } from './GovernorBrief';
import { SeasonRecord, seasonFlows } from '../surface/SeasonManager';

/**
 * A corner on the district's map, in grid coordinates.
 *
 * Structurally `GridCoord`, and deliberately not imported from it: `GridCoord`
 * lives in `TerrainGrid`, and a policy that imported the ground would be a policy
 * that could ask what is standing on it. The whole of what a governor may know
 * about a cell here is that a rectangle would fit at it — which is ADR-015's rule
 * for the siting class (*"the siting class cannot know what it lands on"*) applied
 * one level further up, to the thing that chooses.
 */
export interface SiteCorner {
  readonly x: number;
  readonly y: number;
}

/**
 * Everything the governor is allowed to see.
 *
 * Assembled once per season boundary. Two of the four fields are also in the last
 * Chronicle line; they are here because a district's *first* season has no line and
 * a governor can still see the map — §12's federal overlay draws footprints and
 * interchanges, so a count of each is public in the strongest sense the design has.
 */
export interface DistrictReading {
  /**
   * The season now opening. 1 for a district that has closed none.
   *
   * `seasonOf(cycles)`, taken from the district rather than from the Chronicle's
   * length, because those two disagree in exactly one place — a district
   * mid-season — and this is a decision about the season being entered.
   */
  season: number;
  /** The Chronicle as published so far, oldest first. Empty before settlement one. */
  chronicle: readonly SeasonRecord[];
  /** Facilities standing on this district. */
  facilities: number;
  /** Interchanges standing on this district's roads. §5's corridor, as a count. */
  interchanges: number;
  /** How wide the district is, in cells. A fact §12's federal overlay draws. */
  cols: number;
  /** How deep it is. */
  rows: number;
  /**
   * North-west corners where the next facility's footprint would fit, in grid order.
   *
   * A map fact, and the honest limit of one: the corners are where a rectangle of
   * open ground exists. Nothing in the list says what is growing on it, who lives
   * near it, whether the district deeded it, or what is underneath.
   */
  sites: readonly SiteCorner[];
}

/**
 * One thing a governor does to a district.
 *
 * Data rather than a call, for two reasons that will both matter later. A move is
 * something a test can assert on without running a district — most of
 * `governor.test.ts` is a reading in and a move list out, at no simulation cost.
 * And the seat that *applies* a move is the only place that knows which moves are
 * verbs through the command log and which are dials the district is told
 * (conventions §3); a policy that called the simulation directly would be making
 * that decision in twenty places.
 *
 * The two `prices` fields are the world's rather than a governor's, and they are on
 * this union anyway because in this build there is exactly one object above the
 * district and it is this one. `GovernorSeat.apply` routes them to
 * `SurfaceSim.postMarket`, which is where a world server will take them back.
 */
export type GovernorMove =
  | {
      readonly kind: 'prices';
      readonly computePrice?: number;
      readonly goodsPrice?: number;
      readonly bondYield?: number;
    }
  | { readonly kind: 'tithe'; readonly rate: number }
  | { readonly kind: 'site'; readonly facility: FacilityTypeName; readonly x: number; readonly y: number }
  | { readonly kind: 'freight'; readonly legsPerCycle: number };

/**
 * Anything that can play a district from above.
 *
 * One method, because a season is the only clock a governor has. The interface
 * exists so that the *harness* is not the resident governor: roadmap item 2 names
 * an LLM loop as the other implementation, and §13's read–decide–write is exactly
 * this signature with a model in the middle. Such a policy would be asynchronous
 * and would answer a season late, which is what deadlines are for and why the
 * shape here is "hand it the record, take a list of moves" rather than "let it
 * touch the district".
 */
export interface GovernorPolicy {
  /** Decide this season's moves. Called once per season boundary, before the tick. */
  openSeason(reading: DistrictReading): readonly GovernorMove[];
}

/** Two interchanges is the whole of what §5 requires of a corridor. */
const CORRIDOR_INTERCHANGES = 2;

/**
 * A percentage, as §18's `set_tithe(district, pct)` means one.
 *
 * Rounded to whole percentage points because that is the unit the design writes
 * rates in and the unit the news ticker prints, and a governor whose dial said
 * 0.2334 would be a governor whose published rate and its own arithmetic were
 * different sentences.
 */
function wholePercent(rate: number): number {
  return Math.round(rate * 100) / 100;
}

/**
 * The config-file governor.
 *
 * Constructed with a brief and played by `GovernorSeat`. Two of its four moves —
 * the tithe and the seizure — are answers to the same measured quantity, which is
 * the arrangement §8 describes: *"The tithe is the lever within reach"*, and the
 * other lever is somebody's land.
 */
export class ResidentGovernor implements GovernorPolicy {
  constructor(private brief: GovernorBrief) {}

  /** The instructions this governor is playing under. */
  getBrief(): GovernorBrief {
    return this.brief;
  }

  /**
   * Change the instructions.
   *
   * The brief is not simulation state — it is the polity's own paperwork — so this
   * is a plain setter with no verb and no tick, exactly like a settings slider. What
   * it cannot do is act: a brief edited mid-season changes what the *next* boundary
   * decides and nothing about the season in progress, because a season's tithe was
   * levied against a rate that was already in force.
   */
  setBrief(partial: Partial<GovernorBrief>): void {
    this.brief = { ...this.brief, ...partial };
  }

  openSeason(reading: DistrictReading): readonly GovernorMove[] {
    const moves: GovernorMove[] = [];

    // ── Published, not decided ────────────────────────────────────────────────
    const posting = this.postingFor(reading.season);
    if (posting) {
      moves.push({
        kind: 'prices',
        computePrice: posting.computePrice,
        goodsPrice: posting.goodsPrice,
        bondYield: posting.bondYield,
      });
    }

    const closed = reading.chronicle[reading.chronicle.length - 1];
    const before = reading.chronicle[reading.chronicle.length - 2];
    // The season's flows, which is how every reader of this Chronicle turns levels
    // and lifetime totals into what happened (`seasonFlows`). A district with no
    // record has nothing measured, and a governor with nothing measured has nothing
    // to decide — which is why a district's first season is untithed.
    const flows = closed ? seasonFlows(closed, before) : undefined;

    if (flows) {
      // What the district came to, from the polity's side of the ledger: what was
      // remitted, plus what the estate made here, less what building it cost.
      // §8's treasury has all three lines — *"In: facility operating profits,
      // tithes […] Out: facility construction and opex"* — and this is the whole
      // of it that a district's Chronicle can supply.
      //
      // The capex term is what makes a seizure *hurt the seizer* for the seasons
      // it is being built: a polity spending $36,000 a season on lorries is a
      // polity whose appetite is unmet by more than its whole target, so the dial
      // goes to the ceiling and stays there until the machine hall opens. That is
      // §8's crisis shape arriving from an investment rather than from a shock,
      // and no line of the brief is about it.
      const shortfall =
        this.brief.REVENUE_TARGET -
        (flows.tithePaid + flows.operatingProfit - flows.capitalSpend);

      // ── The extraction dial ─────────────────────────────────────────────────
      //
      // §8: *"One number per district per season: a percentage […] of district tax
      // revenue, remitted upward"*, and the number is what the polity's unmet
      // appetite comes to as a share of what the district measured. A share of
      // nothing is not a decision, so a district that collected no tax is left on
      // whatever rate it was on: the arithmetic has no denominator and the governor
      // has no information.
      if (flows.taxCollected > 0) {
        const wanted = wholePercent(shortfall / flows.taxCollected);
        const rate = Math.min(
          this.brief.TITHE_CEILING,
          Math.max(this.brief.TITHE_FLOOR, wanted)
        );
        // A verb, so it is issued only when it changes something. Re-stating the
        // standing rate every season would put a refused command in the log
        // forever (`FiscalManager.setTitheRate` returns false for the rate a
        // district is already on) and would tell the district, in the ticker, that
        // its tithe had been set — which it had not.
        if (rate !== titheRateOf(closed)) moves.push({ kind: 'tithe', rate });
      }

      // ── The other lever ─────────────────────────────────────────────────────
      //
      // §7's `FORCE` branch: *"the governor places the rectangle anywhere,
      // immediately, paying only statutory compensation"* — the one governor verb
      // that needs no counterparty, and therefore the only one a build with no
      // Siting Protocol can carry. It fires on the same unmet appetite the dial
      // does, which is the shape §8 asserts: when the money is not coming, a
      // governor reaches for the two things within reach, and one of them is
      // somebody's land.
      if (shortfall > 0 && this.maySite(reading)) {
        const site = this.chooseSite(reading);
        if (site) {
          moves.push({ kind: 'site', facility: BRIEF_FACILITY, x: site.x, y: site.y });
        }
      }
    }

    // ── The corridor ──────────────────────────────────────────────────────────
    //
    // A dial rather than a verb (conventions §3), re-asserted every season: what
    // crosses a district is re-derived from scratch each cycle, so a restored
    // district hauls what it is currently offered rather than replaying an
    // instruction. Offered only where there is somewhere for a truck to enter.
    if (reading.interchanges >= CORRIDOR_INTERCHANGES && this.brief.FREIGHT_LEGS > 0) {
      moves.push({ kind: 'freight', legsPerCycle: this.brief.FREIGHT_LEGS });
    }

    return moves;
  }

  /** The world's posting for a season, if the schedule names one. */
  private postingFor(season: number): PricePosting | undefined {
    for (const posting of this.brief.PRICES) {
      if (posting.season === season) return posting;
    }
    return undefined;
  }

  /**
   * Whether the estate may grow this season.
   *
   * Three bounds from the brief and nothing else. The interval is measured off the
   * Chronicle rather than remembered, which is what keeps this class stateless: a
   * season in which the facility count rose is a season in which somebody sited
   * one, and the record says which season that was.
   */
  private maySite(reading: DistrictReading): boolean {
    if (reading.season < this.brief.ESTATE_FROM_SEASON) return false;
    if (reading.facilities >= this.brief.ESTATE_LIMIT) return false;
    const last = lastSitingSeason(reading.chronicle);
    if (last !== undefined && reading.season - last < this.brief.ESTATE_INTERVAL) {
      return false;
    }
    return true;
  }

  /**
   * Which corner to take: the one nearest the middle of the map.
   *
   * §4's production rule takes a `min` over power, water and staffing, so a slab
   * nobody can drive to yields nothing (`docs/staffing.md`) — and the roads and the
   * families are, in every district this game generates, near the middle. So the
   * rule is one sort, and what makes it worth writing down is everything it does
   * *not* consult: not the bushes on the cells, not the covenant a mayor may have
   * paid for, not the houses nearby, not the portal that may be under it. A
   * governor that avoided any of those would be a governor that could see them, and
   * §7's whole argument about force is that it is cheap because it does not look.
   *
   * Ties break by grid order, because a comparator that ties has to break the tie
   * (rule 4) — two corners equally far from the middle must be chosen between the
   * same way on every machine that replays this. `sites` arrives in grid order and
   * the comparison is strict, so the first of two equals wins.
   *
   * The same arithmetic the scenario harness's scripted governor uses
   * (`Scenario.siteDataCentre`), which is not a coincidence and not shared code:
   * ADR-024 wrote that rule for §9's vignette, and a resident governor that
   * preferred a different corner would have made every measurement taken against
   * the vignette incomparable with a governed run.
   */
  private chooseSite(reading: DistrictReading): SiteCorner | undefined {
    let best: SiteCorner | undefined;
    let bestDistance = Number.POSITIVE_INFINITY;
    const midX = reading.cols / 2;
    const midY = reading.rows / 2;
    for (const site of reading.sites) {
      const dx = site.x - midX;
      const dy = site.y - midY;
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        best = site;
        bestDistance = distance;
      }
    }
    return best;
  }
}

/**
 * The rate a season closed on.
 *
 * The fiscal block is omitted by a district nobody has billed, tithed or rationed
 * (`SeasonRecord`), and an omitted block means nothing has been levied — which is
 * a rate of zero, and is the one place in this class where an absent figure and a
 * zero mean the same thing.
 */
function titheRateOf(record: SeasonRecord): number {
  return record.fiscal?.titheRate ?? 0;
}

/**
 * The last season the estate grew in, or `undefined` if it never has.
 *
 * Walked backwards over consecutive lines, comparing levels — the same subtraction
 * `seasonFlows` does, done here over the whole record because the question is not
 * "what changed last season" but "when did it last change".
 */
function lastSitingSeason(chronicle: readonly SeasonRecord[]): number | undefined {
  for (let i = chronicle.length - 1; i >= 0; i--) {
    const count = chronicle[i].facilities?.count ?? 0;
    const previous = i > 0 ? (chronicle[i - 1].facilities?.count ?? 0) : 0;
    if (count > previous) return chronicle[i].season;
  }
  return undefined;
}

